import React, { useState, useEffect, useCallback, useRef, useLayoutEffect, useMemo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

const getColumnCountDefault = () => {
    const width = window.innerWidth;
    if (width >= 1280) return 6;
    if (width >= 768) return 4;
    if (width >= 640) return 3;
    return 2;
};

interface ProfileVirtualGridProps<T> {
    items: T[];
    renderItem: (item: T) => React.ReactNode;
    getItemId: (item: T) => string;
    filter: (item: T) => boolean;
    onClick?: (item: T) => void;
    activeTab: string;
    getColumnCount?: () => number;
    averageHeight?: number;
}

function ProfileVirtualGrid<T>({
    items,
    renderItem,
    getItemId,
    filter,
    onClick,
    activeTab,
    getColumnCount = getColumnCountDefault,
    averageHeight = 250,
}: ProfileVirtualGridProps<T>) {
    const [filteredItems, setFilteredItems] = useState(items.filter(filter));
    const parentRef = useRef<HTMLDivElement>(null);
    const parentOffsetRef = useRef(0);
    const [key, setKey] = useState(0);

    useEffect(() => {
        setKey(prev => prev + 1);
    }, [activeTab, items.length]);

    useLayoutEffect(() => {
        const updateLayout = () => {
            if (parentRef.current) {
                parentOffsetRef.current = parentRef.current.offsetTop;
                // Force reflow
                parentRef.current.style.display = 'none';
                parentRef.current.style.display = '';
            }
        };
        updateLayout();
        const timeout = setTimeout(updateLayout, 100);
        return () => clearTimeout(timeout);
    }, [activeTab, items]);

    useLayoutEffect(() => {
        const newFilteredItems = items.filter(filter);
        setFilteredItems(newFilteredItems);
    }, [items, filter]);

    const [columnCount, setColumnCount] = useState(getColumnCount());
    const rowCount = useMemo(() => Math.ceil(filteredItems.length / columnCount), [filteredItems.length, columnCount]);

    useLayoutEffect(() => {
        const updateColumnCount = () => {
            setColumnCount(getColumnCount());
        };

        window.addEventListener('resize', updateColumnCount);
        return () => window.removeEventListener('resize', updateColumnCount);
    }, [getColumnCount]);

    const rowVirtualizer = useVirtualizer({
        count: rowCount,
        estimateSize: () => averageHeight,
        overscan: 4,
        scrollMargin: parentOffsetRef.current,
        getScrollElement: () => parentRef.current,
    });

    const columnVirtualizer = useVirtualizer({
        horizontal: true,
        count: columnCount,
        getScrollElement: () => parentRef.current,
        estimateSize: useCallback(() => (rowCount * averageHeight), [items, columnCount]),
        overscan: 100,
    });

    const columnItems = columnVirtualizer.getVirtualItems();

    return (
        <div
            key={`${key}-${activeTab}-${filteredItems.length}-${columnCount}`}
            ref={parentRef}
            style={{ minHeight: '1px' }}
            className="h-full overflow-auto"
        >
            <div
                style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                }}
            >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => (
                    <div
                        key={virtualRow.key}
                        data-index={virtualRow.index}
                        ref={rowVirtualizer.measureElement}
                        className="absolute top-0 left-0 w-full"
                        style={{
                            transform: `translateY(${virtualRow.start - rowVirtualizer.options.scrollMargin}px)`,
                            display: 'flex',
                        }}
                    >
                        {columnItems.map((virtualColumn) => {
                            const itemIndex = virtualRow.index * columnCount + virtualColumn.index;
                            const item = filteredItems[itemIndex];
                            return item ? (
                                <div
                                    key={getItemId(item)}
                                    style={{
                                        width: `${virtualColumn.size}px`,
                                        padding: '0.25rem',
                                    }}
                                    onClick={() => {
                                        if (onClick) {
                                            onClick(item)
                                        }
                                    }}
                                >
                                    {renderItem(item)}
                                </div>
                            ) : (
                                <div
                                    key={`empty-${virtualRow.index}-${virtualColumn.index}`}
                                    style={{
                                        width: `${virtualColumn.size}px`,
                                        padding: '0.25rem',
                                    }}
                                />
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default ProfileVirtualGrid;